import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  accountScope,
  parseProgramProgress,
  parseProgramsList,
  splitProgramsByStatus,
  type Program,
  type ProgramAssigneesPatch,
  type ProgramProgress,
  type ProgramStatus,
  type CopyStore,
} from "@heraion/shared";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { useProgramsInvalidator } from "./programsInvalidator";
import { writeError, type DurableWriteFn } from "../seams";
import { usePendingProgramCopies } from "./usePendingProgramCopies";

// ONE programs-list data layer for every surface (anti-monster S-B):
// mobile's Plan tab and web-rnw's console list both build on this hook,
// so a fetch/parse/mutation fix lands once. The host injects apiFetch
// and (optionally) platform cache adapters; confirm dialogs and error
// alerts stay host-side — this hook reports, it never renders.
//
// Semantics preserved from the mobile original (useProgramsData):
//   - programs: cache-first hydrate, then network refresh (SWR).
//   - progress (PROGPROG markers, opt-in): fetched per runnable id;
//     re-fetched when the runnable id SET changes, per-id errors → null.
//   - refreshSignal: parent-driven tick (pull-to-refresh, AppState,
//     focus) that re-kicks the programs fetch.
//   - mutations re-fetch the list even on failure and RETURN the error
//     (null on success) so each host renders its own failure UX.

export type ProgramsResourceStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "refreshing"
  | "error";

/** Platform cache adapter (AsyncStorage on mobile; web-rnw passes
 *  none). The same CopyStore as every ResourceCache (#398). */
export type ProgramsCache<T> = CopyStore<T>;

export type ProgressByProgram = Record<string, ProgramProgress | null>;

export type UseProgramsOptions = {
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Fetch the per-program PROGPROG progress markers (the athlete run
   *  surface needs them; the console list doesn't). Default false. */
  withProgress?: boolean;
  programsCache?: ProgramsCache<Program[]>;
  progressCache?: ProgramsCache<ProgressByProgram>;
  /** Bump to re-kick the programs fetch (pull-to-refresh, focus…). */
  refreshSignal?: number;
  /** Ids the caller FOLLOWS (useFollows). Their bucket is decided by the
   *  follow, not by the author's status. A host that doesn't follow
   *  anything, or hasn't been taught about follows, omits it and buckets
   *  exactly as before. */
  followedProgramIds?: ReadonlySet<string>;
  /** False: no fetch at all, the list stays null (#336: the program editor
   *  needs it on the phone only). Default true. */
  enabled?: boolean;
};

export function usePrograms(opts: UseProgramsOptions) {
  const { withProgress = false, refreshSignal } = opts;
  const [programs, setPrograms] = useState<Program[] | null>(null);
  const [programsStatus, setProgramsStatus] =
    useState<ProgramsResourceStatus>("idle");
  const [programsError, setProgramsError] = useState<Error | null>(null);
  const [progress, setProgress] = useState<ProgressByProgram | null>(null);
  const [progressStatus, setProgressStatus] =
    useState<ProgramsResourceStatus>("idle");
  // Latest progress, for the refetch merge below (preserve a known marker
  // when a per-id fetch transiently fails — see the effect).
  const progressRef = useRef<ProgressByProgram | null>(null);
  progressRef.current = progress;

  // apiFetch + caches via refs: hosts may pass fresh literals per render
  // and none of the effects below should re-run for that (the unstable-
  // object-dep refetch loop, the hard way — see useAsyncResource).
  const apiRef = useRef(opts.apiFetch);
  const writeRef = useRef(opts.durableWrite);
  const programsCacheRef = useRef(opts.programsCache);
  const progressCacheRef = useRef(opts.progressCache);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    writeRef.current = opts.durableWrite;
    programsCacheRef.current = opts.programsCache;
    progressCacheRef.current = opts.progressCache;
  });

  // Single-flight. A read in flight is joined only by the hook's own
  // triggers (a change notice, the mount, the refresh tick), and only if it
  // started AFTER the last known change (`changeSeq`): then it already sees
  // the write. One that started before may return the old list, so a call
  // after a change opens a fresh read, and only the newest read is applied.
  // The exported refreshPrograms never joins: a host calls it after a write
  // of its own that changeSeq does not know about.
  const changeSeqRef = useRef(0);
  const inFlightRef = useRef<{ seq: number; readId: number; p: Promise<void> } | null>(null);
  const latestReadRef = useRef(0);
  // A network answer has been applied: the mount's cache must not cover it.
  const netAppliedRef = useRef(false);
  const readPrograms = useCallback((join: boolean): Promise<void> => {
    const cur = inFlightRef.current;
    if (join && cur && cur.seq === changeSeqRef.current) return cur.p;
    const seq = changeSeqRef.current;
    const readId = ++latestReadRef.current;
    setProgramsStatus((prev) => (prev === "loaded" ? "refreshing" : "loading"));
    setProgramsError(null);
    const scope = accountScope(); // #398: the account this read is for
    const p = (async () => {
      try {
        const raw = await apiRef.current<unknown>("/api/me/programs");
        if (readId !== latestReadRef.current) return;
        const parsed = parseProgramsList(raw);
        netAppliedRef.current = true;
        setPrograms(parsed);
        setProgramsStatus("loaded");
        void programsCacheRef.current?.write(parsed, scope);
      } catch (e) {
        if (readId !== latestReadRef.current) return;
        setProgramsStatus("error");
        setProgramsError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (inFlightRef.current?.readId === readId) inFlightRef.current = null;
      }
    })();
    inFlightRef.current = { seq, readId, p };
    return p;
  }, []);
  const refreshPrograms = useCallback(
    (): Promise<void> => readPrograms(false),
    [readPrograms],
  );

  const enabled = opts.enabled ?? true;

  // A write made through THIS instance: re-read here, tell the others.
  const invalidator = useProgramsInvalidator();
  const selfRef = useRef<() => void>(() => {});
  const onElsewhere = useCallback(() => {
    changeSeqRef.current += 1;
    void readPrograms(true);
  }, [readPrograms]);
  selfRef.current = onElsewhere;
  const programsChanged = useCallback(() => {
    changeSeqRef.current += 1;
    void readPrograms(true);
    invalidator?.invalidate(selfRef.current);
  }, [readPrograms, invalidator]);
  useEffect(() => {
    if (!enabled || !invalidator) return;
    return invalidator.subscribe(onElsewhere);
  }, [enabled, invalidator, onElsewhere]);

  // Disabled, the instance hears no notices: a read in flight from before
  // may miss a write made meanwhile, so the mount on re-enable must not join it.
  useEffect(() => {
    if (!enabled) changeSeqRef.current += 1;
  }, [enabled]);

  // Mount: hydrate from the platform cache (if any), then refresh. The cache
  // never covers a network answer that landed while it was being read.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      const cached = await programsCacheRef.current?.read();
      if (cancelled) return;
      if (cached != null && !netAppliedRef.current) {
        setPrograms(cached);
        setProgramsStatus("loaded");
      }
      void readPrograms(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [readPrograms, enabled]);

  // Parent-driven refresh tick. Skips the initial value — the mount
  // effect already fetched.
  useEffect(() => {
    if (!enabled || refreshSignal === undefined || refreshSignal === 0) return;
    void readPrograms(true);
  }, [refreshSignal, readPrograms, enabled]);

  // Unfollowing re-reads through the change notice (#377, #380): the gate
  // and useLibrary emit it once the follow DELETE has succeeded. Watching
  // followedProgramIds here fired on the optimistic flip, before the
  // DELETE, and could re-read the row still alive.

  const {
    followed: followedPrograms,
    published: publishedPrograms,
    drafts: draftPrograms,
  } = useMemo(
    () => splitProgramsByStatus(programs ?? [], opts.followedProgramIds),
    [programs, opts.followedProgramIds],
  );

  // Progress is keyed by the SORTED followed id set: a changed set
  // (created/deleted/followed program) re-fetches; a same-set list
  // refresh doesn't (mirrors the original mobile keying). Only followed
  // programs show "next up" surfaces, so they're the only ones whose
  // PROGPROG markers are worth fetching.
  const idsKey = useMemo(
    () =>
      followedPrograms
        .map((p) => p.id)
        .sort()
        .join(","),
    [followedPrograms],
  );
  const idsRef = useRef<string[]>([]);
  idsRef.current = followedPrograms.map((p) => p.id).sort();
  const progressHydratedRef = useRef(false);

  useEffect(() => {
    if (!withProgress) return;
    let cancelled = false;
    void (async () => {
      const scope = accountScope(); // #398: the account the merged map is for
      if (!progressHydratedRef.current) {
        progressHydratedRef.current = true;
        const cached = await progressCacheRef.current?.read();
        if (!cancelled && scope.current() && cached != null) {
          setProgress(cached);
          setProgressStatus("loaded");
        }
      }
      const ids = idsRef.current;
      if (cancelled || ids.length === 0) return;
      setProgressStatus((prev) =>
        prev === "loaded" ? "refreshing" : "loading",
      );
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const raw = await apiRef.current<unknown>(
              `/api/me/programs/${encodeURIComponent(id)}/progress`,
            );
            return { id, value: parseProgramProgress(raw), ok: true };
          } catch {
            return { id, value: null as ProgramProgress | null, ok: false };
          }
        }),
      );
      // #423: progressRef holds the map of the account the reads were for.
      if (cancelled) return;
      if (!scope.current()) {
        // The map in memory is the old account's: drop it, and the
        // "loading" this run set, which nothing else would clear.
        setProgress(null);
        setProgressStatus("idle");
        return;
      }
      // Merge, don't replace: a per-id fetch that FAILED must not wipe a
      // previously-known marker to null — that made a recently-trained
      // program look never-run and reordered the Plan carousel (web has no
      // progress cache to backstop it, so a single transient failure flipped
      // the lead card; Antenore 2026-06-18). A SUCCESSFUL null is genuine
      // (never started) and is kept.
      const prev = progressRef.current;
      const map: ProgressByProgram = {};
      for (const e of entries) {
        map[e.id] = e.ok ? e.value : (prev?.[e.id] ?? null);
      }
      setProgress(map);
      setProgressStatus("loaded");
      void progressCacheRef.current?.write(map, scope);
    })();
    return () => {
      cancelled = true;
    };
    // refreshSignal is in the deps so the parent-driven tick (pull-to-
    // refresh, AppState 'active', Plan-tab focus) re-fetches the PROGPROG
    // markers too — NOT just the programs list. Without it, completing a
    // program session advanced the server marker but the client kept the
    // stale pointer (idsKey, the followed-id SET, hasn't changed), so the
    // card recomputed the SAME "next up" and the cycle never rolled over
    // (e.g. finish D5 of a 5-day program, it stays on D5 instead of
    // restarting at D1 — Antenore 2026-06-29, inbox 305e961c). The merge
    // above keeps the SWR semantics (no flash, no carousel reorder).
  }, [withProgress, idsKey, refreshSignal]);

  // Mutations re-fetch whatever the outcome (the server may have partially
  // moved) and hand the error back instead of rendering it. A queued write
  // returns null: durable on mobile, so not an error (DX-2/F).
  const deleteProgram = useCallback(
    async (id: string): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "DELETE",
        path: `/api/me/programs/${encodeURIComponent(id)}`,
      });
      programsChanged();
      return writeError(result);
    },
    [programsChanged],
  );

  // Status moves cover BOTH axes: follow (active↔archived) AND lifecycle
  // (draft↔archived, i.e. publish / un-publish) — the server's status-only
  // endpoint already validates against the full ProgramStatus enum.
  const setProgramStatus = useCallback(
    async (id: string, status: ProgramStatus): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "PATCH",
        path: `/api/me/programs/${encodeURIComponent(id)}/status`,
        body: { status },
      });
      programsChanged();
      return writeError(result);
    },
    [programsChanged],
  );

  // CO-21 — the assignees-only partial (packages/shared's twin of the
  // status-only PATCH above): the standalone Assign sheet on a Library card
  // saves just `assignedTo`/`assigneeEmails`, never the whole program, so it
  // doesn't need the full editor's draft/build machinery. Same shape as
  // setProgramStatus: PATCH, re-fetch regardless of outcome, hand the error
  // back instead of rendering it.
  const setProgramAssignees = useCallback(
    async (id: string, body: ProgramAssigneesPatch): Promise<Error | null> => {
      const result = await writeRef.current({
        method: "PATCH",
        path: `/api/me/programs/${encodeURIComponent(id)}/assignees`,
        body,
      });
      programsChanged();
      return writeError(result);
    },
    [programsChanged],
  );

  // #475: copies a queued write promised and the list does not show yet.
  const { pendingKeys, holdCopy } = usePendingProgramCopies(programs);

  return {
    programs: programs ?? [],
    pendingCopyKeys: pendingKeys,
    holdCopy,
    followedPrograms,
    publishedPrograms,
    draftPrograms,
    progress,
    programsStatus,
    progressStatus,
    programsError,
    refreshPrograms,
    programsChanged,
    deleteProgram,
    setProgramStatus,
    setProgramAssignees,
  };
}

export type ProgramsState = ReturnType<typeof usePrograms>;
